import type { AssignableRole } from "@unbox/api";
import { deriveDisplayRole } from "@unbox/api";
import { Edit01, Mail05, Trash01, Users01 } from "@untitledui/icons";
import { type ReactNode, useMemo } from "react";
import { Table } from "@/components/application/table/table";
import { Avatar } from "@/components/base/avatar/avatar";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { useSort } from "@/hooks/use-sort";
import { cx } from "@/utils/cx";
import { formatDate } from "@/utils/date-conversion";
import { sortTeamRows, type TeamSort, type TeamSortColumn } from "./sort-team-rows";
import { TeamRoleBadge } from "./team-roles";

// Shared by the merchant Team page and the workspace one (UNB-1958), which shows
// the same rows plus a Brands column — passed in as `extraColumn` rather than
// forking the table.

type TeamRowShared = {
  /** Unique across members and invites — members and invites are listed together. */
  id: string;
  email: string;
  /**
   * Every role this row's access is made of — one on a brand page, one per brand
   * on the workspace one. The viewer needs authority over all of them to act on
   * the row.
   */
  roles: AssignableRole[];
};

export type TeamMemberRow = TeamRowShared & {
  kind: "member";
  userId: string;
  name: string;
  lastActivity: Date | null;
  createdAt: Date;
  /** Access comes from a workspace role, so a brand page can't change it. */
  managedAtWorkspaceLevel: boolean;
  isSelf: boolean;
};

export type TeamInviteRow = TeamRowShared & {
  kind: "invite";
  inviteId: string;
  createdAt: Date;
  expired: boolean;
};

export type TeamRow = TeamMemberRow | TeamInviteRow;

export type TeamTableColumn = {
  id: string;
  label: string;
  render: (row: TeamRow) => ReactNode;
};

type TeamTableProps = {
  rows: TeamRow[];
  /**
   * Roles the viewer may grant — `usePermissions().invitableRoles`. A row is
   * actionable only when the viewer has authority over the role it holds, which
   * is the same rule the API enforces with `canAssignRole`.
   */
  assignableRoles: AssignableRole[];
  onEdit: (row: TeamRow) => void;
  onRemove: (row: TeamRow) => void;
  onResend: (row: TeamInviteRow) => void;
  /** Rendered between Role and Status — the Brands column in UNB-1958. */
  extraColumn?: TeamTableColumn;
  emptyMessage?: string;
  /** The order the table opens in. The viewer can change it from the column headers. */
  initialSort?: TeamSort;
};

/** Initials for an account that has a name, falling back to the email local part. */
const initialsFor = (email: string, name?: string) => {
  const fromName = (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
  if (fromName) return fromName.toUpperCase();

  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase() || "U";
};

const relativeDays = (date: Date): string => {
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};

const WORKSPACE_MANAGED = "This person's access is managed at workspace level";
const ROLE_NOT_ASSIGNABLE = "Only a Merchant Admin can manage this person";

const COLUMN_WIDTHS = {
  name: "w-[36%] pl-6 pr-4",
  role: "w-[20%] px-2",
  status: "w-[26%] px-4",
  actions: "w-[18%] pl-2 pr-4",
};

const COLUMN_WIDTHS_WITH_EXTRA = {
  name: "w-[28%] @max-[720px]:w-[30%] pl-6 pr-4",
  role: "w-[17%] @max-[720px]:w-[20%] px-2",
  extra: "w-[17%] px-2 @max-[720px]:hidden",
  status: "w-[23%] @max-[720px]:w-[30%] px-4",
  actions: "w-[15%] @max-[720px]:w-[20%] pl-2 pr-4",
};

export const TeamTable = ({
  rows,
  assignableRoles,
  onEdit,
  onRemove,
  onResend,
  extraColumn,
  emptyMessage = "Team members with access to this brand will appear here.",
  initialSort = { column: "name", direction: "asc" },
}: TeamTableProps) => {
  const sort = useSort<TeamSortColumn>(initialSort.column, initialSort.direction);
  const sortedRows = useMemo(() => sortTeamRows(rows, sort.sort), [rows, sort.sort]);

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <div className="flex items-center justify-center size-14 rounded-full bg-tertiary">
          <Users01 className="size-6 text-secondary" />
        </div>
        <p className="text-sm text-tertiary max-w-xs text-center">{emptyMessage}</p>
      </div>
    );
  }

  /** Why this row can't be edited/removed, or undefined when it can. */
  const blockedReason = (row: TeamRow): string | undefined => {
    if (row.kind === "member" && row.managedAtWorkspaceLevel) return WORKSPACE_MANAGED;
    if (!row.roles.every((role) => assignableRoles.includes(role))) return ROLE_NOT_ASSIGNABLE;
    return undefined;
  };

  // The design stripes alternate rows, and react-aria's render prop hands over the
  // item rather than its position.
  const positionById = new Map(sortedRows.map((row, index) => [row.id, index]));

  const columns = extraColumn ? COLUMN_WIDTHS_WITH_EXTRA : COLUMN_WIDTHS;

  return (
    <div className="@container">
      <Table
        aria-label="Team members"
        className="table-fixed min-w-0"
        sortDescriptor={sort.descriptor}
        onSortChange={sort.setFromDescriptor}
      >
        {/* The shared header defaults to a grey fill; this table's is white. */}
        <Table.Header className="bg-primary">
          <Table.Head id="name" label="Name" isRowHeader allowsSorting className={columns.name} />
          <Table.Head id="role" label="Role" allowsSorting className={columns.role} />
          {extraColumn ? (
            <Table.Head id={extraColumn.id} label={extraColumn.label} className={COLUMN_WIDTHS_WITH_EXTRA.extra} />
          ) : null}
          <Table.Head id="status" label="Status" allowsSorting className={columns.status} />
          <Table.Head id="actions" label="" className={columns.actions} />
        </Table.Header>

        <Table.Body items={sortedRows}>
          {(row) => {
            const blocked = blockedReason(row);
            const cannotRemoveSelf = row.kind === "member" && row.isSelf;

            return (
              <Table.Row id={row.id} className={(positionById.get(row.id) ?? 0) % 2 === 0 ? "bg-secondary_subtle" : ""}>
                <Table.Cell className={columns.name}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar
                      size="md"
                      initials={initialsFor(row.email, row.kind === "member" ? row.name : undefined)}
                      alt={row.kind === "member" && row.name ? row.name : row.email}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      {row.kind === "member" && row.name ? (
                        <>
                          <p className="text-sm font-medium text-primary truncate">{row.name}</p>
                          <p className="text-sm text-tertiary truncate">{row.email}</p>
                        </>
                      ) : (
                        <p className="text-sm font-medium text-primary truncate">{row.email}</p>
                      )}
                    </div>
                  </div>
                </Table.Cell>

                <Table.Cell className={cx("whitespace-nowrap", columns.role)}>
                  <TeamRoleBadge role={deriveDisplayRole(row.roles)} />
                </Table.Cell>

                {extraColumn ? (
                  <Table.Cell className={COLUMN_WIDTHS_WITH_EXTRA.extra}>{extraColumn.render(row)}</Table.Cell>
                ) : null}

                <Table.Cell className={cx("whitespace-nowrap", columns.status)}>
                  <TeamStatusCell row={row} canResend={!blocked} onResend={onResend} />
                </Table.Cell>

                <Table.Cell className={cx("whitespace-nowrap", columns.actions)}>
                  <div className="flex items-center justify-end gap-0.5">
                    {row.kind === "invite" && (
                      <ButtonUtility
                        size="xs"
                        color="tertiary"
                        icon={Mail05}
                        tooltip={blocked ?? "Resend invite"}
                        isDisabled={!!blocked}
                        onClick={() => onResend(row)}
                      />
                    )}
                    <ButtonUtility
                      size="xs"
                      color="tertiary"
                      icon={Trash01}
                      tooltip={
                        blocked ??
                        (cannotRemoveSelf
                          ? "You cannot remove your own access"
                          : row.kind === "invite"
                            ? "Revoke invite"
                            : "Remove from brand")
                      }
                      isDisabled={!!blocked || cannotRemoveSelf}
                      onClick={() => onRemove(row)}
                    />
                    <ButtonUtility
                      size="xs"
                      color="tertiary"
                      icon={Edit01}
                      tooltip={blocked ?? "Edit role"}
                      isDisabled={!!blocked}
                      onClick={() => onEdit(row)}
                    />
                  </div>
                </Table.Cell>
              </Table.Row>
            );
          }}
        </Table.Body>
      </Table>
    </div>
  );
};

const TeamStatusCell = ({
  row,
  canResend,
  onResend,
}: {
  row: TeamRow;
  canResend: boolean;
  onResend: (row: TeamInviteRow) => void;
}) => {
  if (row.kind === "member") {
    return (
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-brand-tertiary_alt">Active</span>
        <span className="text-sm text-tertiary truncate">
          {row.lastActivity ? `Last active ${relativeDays(row.lastActivity)}` : formatDate(row.createdAt)}
        </span>
      </div>
    );
  }

  if (row.expired) {
    return (
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-warning-primary">Invite expired</span>
        <span className="text-sm text-tertiary">
          {formatDate(row.createdAt)}
          {canResend && (
            <>
              {" - "}
              <button
                type="button"
                onClick={() => onResend(row)}
                className="cursor-pointer underline hover:text-secondary"
              >
                Resend
              </button>
            </>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-w-0">
      <span className="text-sm font-medium text-utility-blue-light-500">Invite sent</span>
      <span className="text-sm text-tertiary truncate">{formatDate(row.createdAt)}</span>
    </div>
  );
};
